<template>
	<v-card class="event-card elevation-0 d-flex flex-column">
		<div class="event-card__container">
			<div class="event-card__outer">
				<div class="d-flex event-card__list pl-0 pr-0">
					<Banner v-if="banner && !isLoading" :banner="banner" />
					<EventDescription class="mt-5" :event="event" />
					<div v-if="this.description" class="event-card__section">
						<v-card-text class="event-card__section-title mb-3"> Details</v-card-text>
						<v-card-text
							ref="contentInner"
							class="event-card__description pt-2"
							v-html="description || ''"
							:class="{ collapsed: !isExpanded && isContentTooLong }"
						/>
						<div v-if="!isExpanded && isContentTooLong" class="fade-overlay"></div>
						<div v-if="isContentTooLong" class="event-card__description__show-more">
							<button @click="toggleExpand">
								{{ isExpanded ? 'Hide post' : 'See More' }}
							</button>
						</div>
					</div>
					<div class="event-card__section" v-if="attendees.length && isAttendeesVisible">
						<div class="d-flex justify-space-between">
							<v-card-text class="event-card__section-title mb-3">
								Attendees</v-card-text
							>
							<div class="event-card__show-btn" @click="handleClickAttendees">
								<router-link
									:to="{
										name: 'attendees',
										params: { id: this.event.event_id },
									}"
								>
									See All
								</router-link>
							</div>
						</div>

						<div class="d-flex flex-wrap justify-space-around">
							<div
								v-for="(user, index) in attendees.slice(0, 7)"
								:key="index"
								@click="handleClickAttendee(user.user_id)"
							>
								<Avatar
									class="event-card__avatar border-radius-50"
									:size="60"
									:avatar="user.user_logo"
								/>
							</div>

							<div
								v-if="attendees.length > 7"
								class="event-card__attendees-last-container"
							>
								<div class="event-card__attendees-last-member">
									+{{ totalCountAttendees - 7 }}
								</div>
							</div>
						</div>
					</div>
					<div
						v-if="this.event.eventActions && event.eventActions.length > 0"
						class="event-card__section mb-2"
					>
						<div>
							<v-card-text class="event-card__section-title mb-3">
								Useful Information
							</v-card-text>

							<div class="event-card__event-actions">
								<ActionPreview
									v-for="(action, index) in this.event.eventActions"
									:key="index"
									class="event-card__event-actions__event-wrapper"
									:event-id="event.event_id || event.id"
									:action="action"
									@click.native="handleEventActionClick(action, $event)"
								>
									<div class="event-card__action-wrapper">
										<Icon
											:type="getAction(action).icon"
											color="var(--accent-color)"
											:size="24"
										/>
									</div>
									<div
										class="event-card__event-actions__title text-decoration-none"
									>
										{{ getAction(action).name }}
									</div>
								</ActionPreview>
							</div>
						</div>
					</div>
					<div
						class="event-card__section"
						v-if="partners && partners.length > 0"
						v-for="(
							{ group_name = '', partners = [], group_id = 0, partner_per_group = 6 },
							index
						) in this.event.partner_groups"
						:key="index"
					>
						<div class="d-flex justify-space-between">
							<v-card-text class="event-card__section-title mb-3">
								{{ group_name }}
							</v-card-text>
							<div
								class="event-card__show-btn"
								@click="handleShowAllExhibitors(group_name)"
							>
								<router-link
									:to="{
										name: 'partners',
										params: { id: group_id, name: group_name },
										query: {
											event: event.event_id,
											name: group_name,
										},
									}"
								>
									See All
								</router-link>
							</div>
						</div>

						<div class="event-card__exhibitors-container-wrapper">
							<div
								v-for="(partner, index) in partners.slice(0, partner_per_group)"
								:key="index"
								@click="handleExhibitorClick(partner)"
								class="event-card__exhibitors-container-wrapper__exhibitors-item"
							>
								<router-link
									class="event-card__exhibitors-container-wrapper__link"
									:to="{
										name: 'exhibitor-by-id',
										params: {
											id: partner.partner_id,
										},
										query: {
											group: group_id,
											event: event.event_id,
										},
									}"
								>
									<img
										:src="partner.partner_logo"
										alt="partner_logo"
										class="event-card__exhibitors-container-wrapper__exhibitors-item__exhibitors-logo"
										loading="lazy"
									/>
								</router-link>
							</div>
						</div>
						<div v-if="event && (event.event_map_url || event.event_map)" class="mt-3">
							<button
								type="button"
								@click="toggleOpenMap(event.event_map_url || event.event_map)"
								class="event-card__section__map-btn"
							>
								Floor Map
								<Icon type="chevron_right" :size="18" />
							</button>
						</div>
					</div>
					<div class="event-card__section" v-if="speakers.length">
						<div class="d-flex justify-space-between mb-3">
							<v-card-text class="event-card__section-title"> Speakers</v-card-text>
							<div class="event-card__show-btn">
								<router-link
									v-if="speakersShowAllButton"
									:to="{
										name: 'event-speakers',
										params: { id: this.event.event_id },
									}"
									@click.native="handleShowAllSpeakers"
								>
									See All
								</router-link>
							</div>
						</div>
						<div class="d-flex justify-space-around">
							<div v-for="speaker in speakers.slice(0, 7)" :key="speaker.speaker_id">
								<router-link
									:to="{
										name: 'speaker-by-id',
										params: {
											id: speaker.speaker_id,
										},
									}"
								>
									<Avatar
										class="event-card__avatar mr-2 mb-1 border-radius-50"
										:size="60"
										:avatar="speaker.speaker_logo"
									/>
								</router-link>
							</div>
							<div
								v-if="speakers.length > 7"
								class="event-card__attendees-last-container"
							>
								<div class="event-card__attendees-last-member">
									+{{ speakers.length - 7 }}
								</div>
							</div>
						</div>
					</div>
					<div v-if="polls.length" class="event-card__section">
						<div>
							<v-card-text class="event-card__section-title mb-3">
								Survey(s)</v-card-text
							>
							<PollEvent
								v-for="poll in polls"
								:key="poll.poll_id"
								:poll="poll"
								:answers="polls_answers"
								:isEventVoteDirty="isVoteDirty"
								:pollsLoading="pollsLoading"
								@answerChange="answerChange"
							/>
							<v-btn
								v-if="!isPollsVoted"
								class="event-card__poll-btn"
								:class="{ 'event-card__poll-btn-disabled': voteDisabled }"
								:ripple="false"
								@click="handleVote"
								:loading="pollsLoading"
							>
								Vote
							</v-btn>
						</div>
					</div>
					<div class="event-card__section" v-if="ratings.length">
						<div>
							<v-card-text class="event-card__section-title mb-3">
								Rating(s)</v-card-text
							>
							<Rating
								v-for="rating in ratings"
								:key="rating.rating_id"
								:rating="rating"
								@ratingChange="ratingChange"
							/>
							<v-btn
								v-if="isRatingsAvailable"
								class="event-card__rating-btn"
								:class="{ 'event-card__rating-btn-disabled': ratingDisabled }"
								:ripple="false"
								@click="handleRate"
								:loading="ratingsLoading"
							>
								Send Rating
							</v-btn>
						</div>
					</div>
				</div>
			</div>
			<v-dialog
				v-model="mobileLink"
				class="exhibitor-page__modal"
				max-width="588px"
				width="100%"
				persistent
			>
				<v-card class="exhibitor-page__modal">
					<div class="exhibitor-page__modal__title">
						This screen is not available in the Web App, but you can access it through
						the mobile app
					</div>
					<button
						type="button"
						@click="toggleCloseMobileLink"
						class="exhibitor-page__modal__btn"
					>
						OK
					</button>
				</v-card>
			</v-dialog>
		</div>
	</v-card>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import { mapActions, mapGetters } from 'vuex'
import Avatar from '@/components/Avatar'
import ActionPreview from '@/components/Event/Action/ActionPreview'
import PollEvent from '@/components/Post/PollEvent.vue'
import Rating from '@/components/Event/Rating'
import EventSpeakerCard from '@/components/Event/EventSpeakerCard'
import Dwh from '@/mixins/Dwh'
import EventDescription from '@/components/Event/EventDescription.vue'
import Banner from '@/components/Banner.vue'

Vue.use(VueScrollTo)

export default {
	mixins: [Dwh],
	components: {
		Banner,
		EventDescription,
		ActionPreview,
		Avatar,
		PollEvent,
		EventSpeakerCard,
		Rating,
	},
	props: {
		event: {
			type: Object,
			default: () => ({}),
		},
	},
	data() {
		return {
			mobileLink: false,
			changeContent: false,
			offset: true,
			description: '',
			polls_answers: [],
			custom_answer: '',
			isVoteDirty: false,
			rating_answers: [],
			isFollowed: false,
			isExpanded: false,
			isContentTooLong: false,
		}
	},
	computed: {
		...mapGetters('auth', ['profile', 'isAuthenticated', 'mapRegisterToken']),
		...mapGetters('event', [
			'isLoading',
			'attendees',
			'totalCountAttendees',
			'pollsLoading',
			'ratingsLoading',
		]),
		...mapGetters('advertisement', ['banner']),
		...mapGetters('settings', ['eventSettings', 'appCore']),

		isExternal() {
			return this.event.event_register_link.startsWith('http')
		},
		sponsorTextAnimationDuration() {
			if (this.event.event_sponsor_text) {
				const duration = this.event.event_sponsor_text.length / 20
				return duration < 5 ? 5 : duration
			} else {
				return null
			}
		},
		sponsorTextAnimation() {
			return this.event.event_sponsor_text.length <= 40 ? 'runningText1' : 'runningText2'
		},
		voteDisabled() {
			return (
				this.pollsLoading ||
				this.polls_answers.length !== this.polls.length ||
				this.polls_answers.some((poll) => poll.error)
			)
		},
		polls() {
			return this.event.polls.filter(
				(poll) =>
					poll.pollsAnswers.length ||
					(!poll.pollsAnswers.length && poll.allow_custom_answer)
			)
		},
		isPollsVoted() {
			return this.event.polls.every((poll) => poll.vote)
		},
		isAttendeesVisible() {
			if (this.eventSettings) {
				return this.eventSettings.show_attendees
			}
			return false
		},
		speakers() {
			return this.event.speakersList
		},
		speakersShowAllButton() {
			return this.event.speakersList.length >= 1
		},
		ratings() {
			return this.event.ratings.filter(
				(rating) => !rating.vote || (rating.vote && rating.display_rating)
			)
		},
		isRatingsAvailable() {
			return this.ratings.some((rating) => !rating.vote)
		},
		availableRatings() {
			return this.ratings.filter((rating) => !rating.vote)
		},
		ratingDisabled() {
			return this.rating_answers.length !== this.availableRatings.length
		},
	},
	watch: {
		'event.event_description'(val) {
			if (val) {
				const parser = new DOMParser()
				const doc = parser.parseFromString(val, 'text/html')
				this.description = doc.body.innerHTML

				this.$nextTick(() => {
					this.checkContentHeight()
				})
			}
		},
	},
	methods: {
		...mapActions('advertisement', ['getBanner', 'clearBanner']),
		...mapActions('post', ['getPost', 'deletePost']),
		...mapActions('event', [
			'addEventToSchedule',
			'removeEventFromSchedule',
			'voteEventPolls',
			'voteEventRatings',
		]),
		...mapActions('app', ['setPageTitle']),
		...mapActions('auth', ['getMapRegisterToken']),
		isMapUrlValid(url) {
			try {
				const arr = JSON.parse(this.appCore.parsing_url)
				const link = new URL(url)
				return arr.some((allowed) => link.origin === allowed)
			} catch (e) {
				console.error(e)
				return false
			}
		},
		toggleCloseMobileLink() {
			this.mobileLink = false
		},
		async toggleOpenMap(url) {
			if (!this.event) return

			const mapUrl = url

			if (mapUrl && this.isMapUrlValid(mapUrl)) {
				try {
					await this.getMapRegisterToken()
					if (!this.mapRegisterToken) {
						console.error('Token not received')
						return
					}
					const urlWithToken = `${mapUrl}${mapUrl.includes('?') ? '&' : '?'}code=${
						this.mapRegisterToken
					}`
					window.open(urlWithToken, '_self')
				} catch (e) {
					console.error(e)
				}
			} else if (mapUrl) {
				window.open(mapUrl, '_self')
			}
		},
		checkContentHeight() {
			const contentEl = this.$refs.contentInner
			if (contentEl) {
				const lineHeight = parseFloat(window.getComputedStyle(contentEl).lineHeight)
				const height = contentEl.scrollHeight
				const numLines = Math.floor(height / lineHeight)
				this.isContentTooLong = numLines > 5
			}
		},

		changeContentFunction() {
			this.changeContent = !this.changeContent
		},
		cutTextToSetCharacters(text, count) {
			if (!text || typeof text !== 'string') return ''
			if (text.length <= count) {
				return text
			} else {
				const parser = new DOMParser()
				const doc = parser.parseFromString(text, 'text/html')
				const truncatedHTML = doc.body.innerHTML.slice(0, count)
				return truncatedHTML + '...'
			}
		},
		getAddressLine(address) {
			return [address.address_city, address.address_state, address.address_country]
				.filter(function (v) {
					return v !== null
				})
				.join(',')
		},
		toggleExpand() {
			this.isExpanded = !this.isExpanded
		},

		getAction(action) {
			let icons = {
				link: 'link',
				email: 'email',
				phone: 'phone',
				grid: 'phone_empty',
				fileList: 'document',
				linkList: 'list',
				deepLink: 'link',
				grid2: 'IconGrid2',
			}
			return {
				icon: icons[action.action_type],
				link:
					action.action_type !== '' && action.action_type !== ''
						? action.action_value
						: null,
				name: action.action_name,
				color: action.button_color,
			}
		},
		handleRegisterEvent() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_register_button_event',
				module: 'Events',
				data: this.event.event_id,
				title: this.event.event_title,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		handleShowAllExhibitors(title) {
			this.sendTrigger({
				category: 'Content',
				action: 'click_exhibitors_list_event',
				module: 'Events',
				data: title,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		handleExhibitorClick(partner) {
			this.sendTrigger({
				category: 'Content',
				action: 'click_exhibitor_profile_event',
				module: 'Events',
				data: partner.partner_id,
				title: partner.partner_name,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		handleClickAttendees() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_attendees_list_event',
				module: 'Events',
				data: this.event.event_id,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		handleClickAttendee(id) {
			this.sendTrigger({
				category: 'Content',
				action: 'click_attendee_profile_event',
				module: 'Events',
				data: id,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		async handleEventActionClick(eventAction, event) {
			let action = null
			switch (eventAction.action_type) {
				case 'email':
					action = 'click_action_email_event'
					break
				case 'phone':
					action = 'click_action_phone_event'
					break
				case 'link':
				case 'deepLink':
					action = 'click_action_link_event'
					break
				case 'linkList':
					action = 'click_action_list_of_links_event'
					break
				case 'fileList':
					action = 'click_action_list_of_files_event'
					break
				default:
					action = null
			}
			if (eventAction.action_type === 'link') {
				try {
					await this.toggleOpenMap(eventAction.action_value)
					return
				} catch (e) {
					console.error(e)
				}
			}

			if (eventAction.action_type === 'deepLink') {
				const restrictedLinks = [
					'live-events',
					'event-manager',
					'higher-logic',
					'gamification',
				]
				const lowerCaseLink = eventAction.action_value.toLowerCase()
				const isRestricted = restrictedLinks.some((restricted) =>
					lowerCaseLink.includes(restricted)
				)

				if (isRestricted) {
					if (event) {
						event.preventDefault()
						event.stopPropagation()
					}
					this.mobileLink = true
					return
				} else {
					const target = this.transformDeepLink(eventAction.action_value)
					window.open(target, '_self')
					return
				}
			}

			if (action) {
				this.sendTrigger({
					category: 'Content',
					action,
					module: 'Events',
					title: eventAction.action_name,
					content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
				})
			}
		},

		transformDeepLink(link) {
			const match = link.match(/:\/\/(.*)/)
			return match ? `${window.location.origin}/webapp/${match[1]}` : link
		},

		handleShowAllSpeakers() {
			this.sendTrigger({
				category: 'Content',
				action: 'click_speakers_list_event',
				module: 'Events',
				data: this.event.event_id,
				title: this.event.event_title,
				content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
			})
		},
		answerChange(answer) {
			if (!answer.answer || (Array.isArray(answer.answer) && !answer.answer.length)) {
				if (this.polls_answers.length) {
					this.polls_answers = this.polls_answers.filter(
						(poll) => poll.poll_id !== answer.poll_id
					)
				}
			} else {
				const pollAnswer = this.polls_answers.find(
					(poll) => poll.poll_id === answer.poll_id
				)
				if (pollAnswer) {
					this.polls_answers = this.polls_answers.map((poll) => {
						if (poll.poll_id === answer.poll_id) {
							return answer
						} else {
							return poll
						}
					})
				} else {
					this.polls_answers = [...this.polls_answers, answer]
				}
			}
		},
		preparePollsPayload() {
			return this.polls_answers
				.map((item) => {
					let answerIds = Array.isArray(item.answer)
						? item.answer.filter((v) => v !== 'custom')
						: typeof item.answer === 'number'
						? [item.answer]
						: []

					const customPicked =
						(Array.isArray(item.answer) && item.answer.includes('custom')) ||
						item.answer === 'custom'

					let payload = { poll_id: item.poll_id }

					if (answerIds.length) payload.poll_answer_id = answerIds
					if (customPicked && item.customAnswer && item.customAnswer.trim().length)
						payload.custom_answer = item.customAnswer.trim()
					if (
						answerIds.length ||
						(payload.custom_answer && payload.custom_answer.length)
					) {
						return payload
					}
					return null
				})
				.filter(Boolean)
		},
		async handleVote() {
			this.isVoteDirty = true
			if (!this.voteDisabled) {
				const answersPayload = this.preparePollsPayload()
				await this.voteEventPolls({
					answers: answersPayload,
					eventId: this.event.event_id,
				})
				this.sendTrigger({
					category: 'Content',
					action: 'click_vote_poll_button_event',
					module: 'Polls',
					data: this.event.event_id,
					content_chapter_id: this.event.chapter_id ? [this.event.chapter_id] : null,
				})
			}
		},
		ratingChange(rating) {
			if (this.rating_answers.find((item) => item.rating_id === rating.rating_id)) {
				this.rating_answers = this.rating_answers.map((item) => {
					if (item.rating_id === rating.rating_id) {
						return {
							...rating,
						}
					} else {
						return item
					}
				})
			} else {
				this.rating_answers = [...this.rating_answers, rating]
			}
		},
		async handleRate() {
			if (!this.ratingDisabled) {
				await this.voteEventRatings({
					ratings: this.rating_answers,
					eventId: this.event.event_id,
				})
			}
		},
	},
	created() {
		if (this.$route.params.id !== undefined) {
			const bannerQuery = {
				places: ['event_general'],
				place_sub_id: this.$route.params.id,
			}
			this.getBanner(bannerQuery)
		}
		if (this.event?.event_description) {
			const parser = new DOMParser()
			const doc = parser.parseFromString(this.event.event_description, 'text/html')
			this.description = doc.body.innerHTML
		} else {
			this.description = ''
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.checkContentHeight()
		})
		this.setPageTitle('Events')
	},
	beforeDestroy() {
		this.setPageTitle('')
	},
}
</script>

<style lang="scss">
.fade-overlay {
	position: absolute;
	bottom: 0;
	left: 0;
	width: 100%;
	height: 30px;
}
.event-card {
	background-color: transparent !important;
	font-family: Montserrat, sans-serif;
	&__event-actions {
		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		gap: 8px;
		&__event-wrapper {
			display: flex;
			flex: 0 0 calc(50% - 8px);
			box-sizing: border-box;
			align-items: center;
			padding: 8px;
			gap: 16px;
			width: 100%;
			background-color: #f3f3f6;
			border-radius: 8px;
		}
		&__title {
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
	}
	&__exhibitors-container-wrapper {
		display: flex;
		justify-content: space-around;
		&__link {
			display: flex;
			justify-content: center;
			align-items: center;
		}
		&__exhibitors-item {
			display: flex;
			align-items: center;
			justify-content: center;
			width: 60px;
			height: 60px;
			&__exhibitors-logo {
				object-fit: contain;
				width: 64px;
				height: 64px;
			}
		}
	}

	&__section {
		padding: 12px;
		background: #fff;
		margin-bottom: 8px;
		border-radius: 8px;
		border: 1px solid #e2e2e2;
		&__map-btn {
			display: flex;
			justify-content: center;
			border-radius: 8px;
			align-items: center;
			gap: 8px;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			width: 100%;
			letter-spacing: 0;
			height: 44px;
			text-align: center;
			border: 1.5px solid var(--accent-color);
			color: var(--accent-color);
		}

		&-title {
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			color: #4a4a4a;
		}
	}

	&__container {
		width: 100%;
		height: 100%;
		border-radius: 10px !important;
		margin-bottom: 30px;
	}

	&__content-inner {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		line-height: 18px;
		padding-top: 10px;

		img {
			width: 100%;
		}

		p {
			margin-bottom: 0;
		}

		h1 {
			font-size: 30px;
			padding-bottom: 8px;
			line-height: 30px;
		}
	}

	&__meta {
		font-family: Montserrat, sans-serif;
		font-style: normal;
		font-weight: bold;
		font-size: 18px;
		line-height: 20px;
		color: rgb(66, 160, 216);
		letter-spacing: 0.36px;
		gap: 4px;

		&-bottom {
			color: rgb(155, 155, 155);
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			line-height: 16px;
		}

		&-time {
			color: rgb(155, 155, 155);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
			text-align: right;
		}
	}

	&__list {
		position: relative;
		flex-direction: column;
	}

	&__title {
		color: rgb(74, 74, 74);
		font-size: 24px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
	}

	&__event-time {
		color: rgb(74, 74, 74);
		font-size: 15px;
		font-family: Montserrat, sans-serif;
		font-weight: 600;
	}

	&__attendees {
		&-member {
			width: 50px;
			height: 50px;
			border-radius: 50%;
			object-fit: cover;
		}

		&-last-member {
			position: absolute;
			top: 0;
			bottom: 0;
			left: 0;
			right: 0;
			z-index: 999;
			background-color: #ffffff;
			border: 1px solid #e2e2e2;
			color: #4a4a4a;
			display: flex;
			justify-content: center;
			align-items: center;
			font-family: Inter, sans-serif;
			font-weight: 400;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;

			width: 60px;
			height: 60px;
			border-radius: 50%;
		}

		&-last-container {
			position: relative !important;
			width: 60px !important;
		}
	}

	&__event {
		&-wrapper {
			display: flex;
			align-items: center;
			gap: 10px;
			width: 250px;
			margin-bottom: 20px;
			margin-right: 10px;

			i {
				border-radius: 50%;
				min-width: 40px;
				min-height: 40px;
			}
		}

		&-title {
			color: var(--ceil-detail-text-color);
			font-size: 16px;
			font-family: Montserrat, sans-serif;
			font-weight: normal;
		}
	}

	&__exhibitors {
		&-container {
			display: grid;
			grid-template-columns: repeat(3, 1fr);
			flex-wrap: wrap;
			align-items: center;
			padding: 10px 20px;
			gap: 16px;
			justify-content: space-between !important;

			@media screen and (max-width: 600px) {
				display: flex;
				flex-wrap: wrap;
				align-items: center;
				padding: 10px 20px;
				gap: 16px;
				justify-content: space-between !important;
			}
		}

		&-more {
			color: var(--accent-color);
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
			text-decoration: underline;
			text-align: right;
		}

		display: flex;
		flex-wrap: wrap;
		justify-content: space-between;
		align-items: center;

		img {
			width: 120px;
		}
	}

	&__subtitle {
		&-category {
			color: rgb(155, 155, 155);
			font-size: 14px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
		}
	}

	&__registered {
		color: rgb(255, 255, 255) !important;
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-align: center;
		background-color: #a0a0a0 !important;
		border-radius: 8px;
		height: 50px !important;
		width: 100%;
	}

	&__register {
		color: rgb(255, 255, 255) !important;
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: bold;
		text-align: center;
		background-color: var(--button-secondary-background-color) !important;
		border-radius: 8px;
		height: 50px !important;
		width: 100%;
	}

	&__action-wrapper {
		width: 40px;
		height: 40px;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	&_brief-description {
		font-family: 'Montserrat', sans-serif;
		font-size: 14px;
		color: #002855;
		line-height: 17.07px;
		font-weight: 400;
	}

	&__description {
		font-family: 'Inter', sans-serif;
		font-weight: 400;
		font-size: 16px;
		line-height: 140%;
		letter-spacing: 0;
		color: #4a4a4a;
		&.collapsed {
			display: -webkit-box;
			-webkit-line-clamp: 5;
			-webkit-box-orient: vertical;
			overflow: hidden;
		}
		&__show-more {
			display: flex;
			justify-content: flex-end;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			color: var(--accent-color);
		}

		table {
			margin: 15px auto;
			border-collapse: collapse;
			font-family: Montserrat, sans-serif;
		}

		table td {
			padding: 15px;
			text-align: center;
		}

		table th {
			padding: 15px;
		}

		table thead td {
			background-color: #54585d;
			color: #ffffff;
			font-weight: bold;
			font-size: 13px;
			border: 1px solid #54585d;
			text-align: center;
		}

		table thead th {
			border: 1px solid #dddfe1;
		}

		table tbody td {
			color: #636363;
			border: 1px solid #dddfe1;
		}

		table tbody th {
			border: 1px solid #dddfe1;
		}

		table tbody tr {
			background-color: #f9fafb;
		}

		table tbody tr:nth-child(odd) {
			background-color: #ffffff;
		}

		img {
			width: 100%;
		}
	}

	&__tag {
		padding: 6px;
		border-radius: 3px !important;
		color: rgb(74, 74, 74);
		font-size: 14px !important;
		font-family: Montserrat, sans-serif;
		font-weight: normal;
		height: 20px !important;
		cursor: pointer;

		span {
			min-width: 100% !important;
			width: 100%;
		}
	}

	&__schedule-btn,
	&__show-btn {
		display: flex;
		justify-content: center;
		width: 64px;
		white-space: nowrap;

		a {
			color: var(--accent-color) !important;
			font-family: Inter, sans-serif;
			font-weight: 700;
			font-size: 18px;
			line-height: 100%;
			letter-spacing: 0;
			text-align: center;
			text-decoration: unset;
		}
	}

	&__poll-btn,
	&__rating-btn {
		width: 100%;
		height: 50px !important;
		border-radius: 8px;
		font-size: 18px !important;
		line-height: 22px;
		font-weight: 700;
		text-transform: capitalize;
		background-color: var(--button-main-background-color) !important;
		color: var(--button-main-color) !important;

		&-disabled {
			opacity: 0.5;
			cursor: default;
		}
	}

	@media screen and (max-width: 1904px) {
		&__block {
			flex: 0 0 100%;
		}

		&__list {
			flex: 0 0 75%;
		}
	}

	@media screen and (max-width: 1264px) {
		&__block {
			flex: 0 0 100%;
		}

		&__list {
			flex: 0 0 66%;
		}
	}

	@media screen and (min-width: 1264px) {
		&__container {
			max-width: 600px;
		}
	}

	@media screen and (max-width: 768px) {
		&__thumbnail {
			height: 100%;
			max-width: 100%;
			border-radius: 10px;

			&-image {
				max-height: 250px;
			}
		}

		&__default-thumbnail {
			height: 100%;
		}

		&__outer {
			flex-direction: column;
		}
	}
}

@keyframes runningText1 {
	// 0 - 40
	0% {
		transform: translateX(100%);
	}

	100% {
		transform: translateX(-50%);
	}
}

@keyframes runningText2 {
	// 50 - 180
	0% {
		transform: translateX(100%);
	}

	100% {
		transform: translateX(-100%);
	}
}
</style>
